import _ from "lodash";

import { RecipientDetailsModel } from "@/Appointments/Models/Appointment/RecipientDetailsModel";
import { PrivateBillCostCenter } from "@/Common/Components/Billing/PrivateBillCostCenter";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { Popup } from "@/Common/Components/Modals/Popup";
import { ClinicianSearchStrategy, PersonSearchAutocompleteCustom } from "@/Common/Components/Search/PersonSearchAutocompleteCustom";
import { BillingPaymentMethodsHelper } from "@/Common/Helpers/BillingPaymentMethodsHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { EnumMyleLoginPermission } from "@/Common/Models/EnumMyleLoginPermission";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";
import { PrivateBill_BilledTo } from "@/Common/Models/UI/Enums";
import { BillableService } from "@/Patient/Components/History/BillableService";
import { BillingBilledToHelper } from "@/Patient/Helpers/BillingBilledToHelper";
import { IBillableService } from "@/Patient/Models/UI/History/IBillableService";
import { IPatientPrivateBillModel } from "@/Patient/Models/UI/History/IPatientPrivateBillModel";
import { RecipientDetails } from "./RecipientDetails";

import "./PrivateBillEditControl.css";

import img_locked from "@/Globals/_images/locked.png";
import img_unlocked from "@/Globals/_images/unlocked.png";

interface IPatientHistoryPrivateBillEditProps {
    Model: IPatientPrivateBillModel;
    BillableServicesModel: IBillableService[];

    TodayClickedOp: number;
    DateOfVisit_Set: number;
    SelectClinician: number;
    DidMountOp: number;
    SiteSelectedOp: number;
    CostCenterChangedOp: number;
    PaymentMethodChangedOp: number;
    DiscountChangedOp: number;
    DiscountTypeChangeOp: number;

    ClearOp: number;
    SelectService: number;
    PriceChange: number;
    QuantityChanged: number;
    ChangeGST: number;
    ChangeQST: number;
    ChangeHST: number;
    ChangeGenericTax: number;
    RemoveService: number;
    NotesChangeOp: number;
    OnCloseOperation?: number;
    OnCancelBillOp?: number;
    BilledToChangeOp: number;

    DoNotRenderAsScrollable?: boolean;

    LockProviderOp: number;

    ChangeRecipientDetailsOp: number;
    onIsFormValidChange: (isFormValid: boolean) => void;
}

interface IPrivateBillEditControlState {
    strategy: ClinicianSearchStrategy;
}

class PrivateBillEditControl extends React.Component<IPatientHistoryPrivateBillEditProps, IPrivateBillEditControlState> {
    public constructor(props: IPatientHistoryPrivateBillEditProps) {
        super(props);
        this.state = { strategy: new ClinicianSearchStrategy(props.Model.get("Clinicians").toArray()) };
    }

    public componentDidMount(): void {
        if (this.props.Model.get("IsEditBill") === false && this.props.BillableServicesModel.length === 0) {
            sessionStore.dispatchAsync(this.props.DidMountOp);
        }
        else {
            this.checkFormValidity();
        }
    }

    public componentDidUpdate(prevProps: IPatientHistoryPrivateBillEditProps): void {
        if (this.props.Model.get("Clinicians") !== prevProps.Model.get("Clinicians"))
            this.setState({ strategy: new ClinicianSearchStrategy(this.props.Model.get("Clinicians").toArray()) });

        if (!_.isEqual(prevProps.BillableServicesModel, this.props.BillableServicesModel) ||
            !_.isEqual(prevProps.Model.get("SelectedBillBilledTo"), this.props.Model.get("SelectedBillBilledTo")) ||
            !_.isEqual(prevProps.Model.get("SelectedClinicians"), this.props.Model.get("SelectedClinicians")) ||
            !_.isEqual(prevProps.Model.get("SelectedDateOfVisit"), this.props.Model.get("SelectedDateOfVisit")) ||
            !_.isEqual(prevProps.Model.get("SelectedBillRecipientDetails"), this.props.Model.get("SelectedBillRecipientDetails"))
        ) {
            this.checkFormValidity();
        }
    }

    private checkFormValidity(): void {
        const {
            Model,
            BillableServicesModel,
            onIsFormValidChange
        } = this.props;

        let isSelectedServicesEmpty: boolean = BillableServicesModel
            ?.map(billService => billService.get("SelectedService"))
            .every(billService => !billService);

        const hasDateOfVisit: boolean = !!Model?.get("SelectedDateOfVisit");

        const hasService: boolean = BillableServicesModel?.map(billService => billService.get("SelectedService"))?.length > 0;

        let isValid = !isSelectedServicesEmpty && this.isProviderFieldValid && hasDateOfVisit && hasService;

        const validatePhoneNumber = (phoneNumber: string): boolean => {
            const phoneNumberRegex = new ValidationHelper()
                .getPhoneNumberValidationRule(cacheStore.SiteCache[0].Model?.Adresses[0].CountryLookupId, null)
                .ValidationRegexPattern;

            return phoneNumber
                ? ValidationHelper.isExactMatch(phoneNumber, new RegExp(phoneNumberRegex))
                : true;
        };

        if (isValid && cacheStore.ClinicSettings.EnablePrivateBillRecipient && Model?.get("SelectedBillBilledTo") == PrivateBill_BilledTo.ThirdParty) {
            const recipientDetails = Model.get("SelectedBillRecipientDetails");
            const recipientName = recipientDetails?.RecipientName;
            const recipientFaxNumber = recipientDetails?.RecipientFaxNumber;
            const recipientPhoneNumber = recipientDetails?.RecipientPhoneNumber;
            isValid = !!recipientName && validatePhoneNumber(recipientFaxNumber) && validatePhoneNumber(recipientPhoneNumber);
        }

        onIsFormValidChange(isValid);
    }

    public render(): JSX.Element {
        const privateBillModel = this.props.Model;
        const costCenterList = this.props.Model.get("CostCenterList");
        const selectedSite = cacheStore.GetSiteById(this.props.Model.get("SelectedSiteId"));

        let hasCostPermission = sessionStore.hasPermission(EnumMyleLoginPermission.BillingCostPermission);

        const paymentMethodDictionnary = cacheStore.ClinicSettings.EnablePrivateBillRecipient ?
            BillingPaymentMethodsHelper.getPaymentMethodsDictionnaryWithoutReceivable() : BillingPaymentMethodsHelper.getPaymentMethodsDictionnary();

        const billToDictionnary = BillingBilledToHelper.getBilledToDictionnary();

        const costCenterId = this.props.Model.get("SelectedCostCenterId") ?? null;

        const showRecipientDetails = cacheStore.ClinicSettings.EnablePrivateBillRecipient && privateBillModel.get("SelectedBillBilledTo") == PrivateBill_BilledTo.ThirdParty;

        let serviceEditComponents: JSX.Element[] = [];
        if (this.props.BillableServicesModel.length > 0) {
            serviceEditComponents = this.props.BillableServicesModel.map((serviceModel: IBillableService) => (
                <BillableService
                    model={serviceModel}
                    costCenterId={costCenterId}
                    ClearOp={this.props.ClearOp}
                    SelectService={this.props.SelectService}
                    PriceChange={this.props.PriceChange}
                    QuantityChanged={this.props.QuantityChanged}
                    ChangeGST={this.props.ChangeGST}
                    ChangeQST={this.props.ChangeQST}
                    ChangeHST={this.props.ChangeHST}
                    ChangeGenericTax={this.props.ChangeGenericTax}
                    RemoveService={this.props.RemoveService}
                    selectedSite={selectedSite}
                    key={serviceModel.get("Id")}
                />
            ));
        }

        let classname = "";
        if (this.props.DoNotRenderAsScrollable != null && this.props.DoNotRenderAsScrollable == true) {
            classname = "main-content private-billing-edit-control";
        }
        else {
            classname = "main-content scrollable no-x-overflow private-billing-edit-control";
        }

        return (
            <div>
                {privateBillModel.get("CancelPopupIsOpen") === true && (
                    <Popup title={cacheStore.GetString("CancelBillLabel")} cssClass="double-booking" closeTarget={this.props.OnCancelBillOp}>
                        <div className="p-body">
                            <div className="row">
                                <div className="col-lg-12">
                                    <span>{cacheStore.GetString("CancelBillMessage")}</span>
                                </div>
                            </div>
                        </div>
                        <div className="p-footer">
                            <div className="row">
                                <div className="col-lg-12 text-right">
                                    <ButtonControl
                                        onClick={() => sessionStore.dispatch(this.props.OnCancelBillOp)}
                                        label={cacheStore.GetString("CancelLabel")}
                                        className="btn btn-default push-right-5"
                                    />
                                    <ButtonControl
                                        onClick={() => sessionStore.dispatch(this.props.OnCloseOperation)}
                                        label={cacheStore.GetString("ContinueLabel")}
                                        className="btn btn-primary"
                                    />
                                </div>
                            </div>
                        </div>
                    </Popup>
                )}

                <div data-hvar="60" className={classname}>
                    <div className="row">
                        <div className="col-md-6">
                            <div className="flex-container private-billing">
                                <div className="title">
                                    <span className="form-label">{cacheStore.GetString("DateOfVisitLabel")}</span>
                                </div>
                                <div className="flex-container">
                                    <DateSelector valid={privateBillModel.get("SelectedDateOfVisit") !== null} id="privateBill_create_date" cssClass="" date={privateBillModel.get("SelectedDateOfVisit")} onSelectOp={this.props.DateOfVisit_Set} dateFormat="dddd, MMM dd, yyyy" disabled={false} />
                                    <div style={{ padding: "10px" }}>
                                        <a className="pointer" id="dp1-today" onClick={e => this.onTodayClick(e)}>{cacheStore.GetString("TodayLabel")}</a>
                                    </div>
                                </div>
                            </div>

                            {cacheStore.SiteCache.length > 1 && (
                                <div className="flex-container private-billing">
                                    <div className="title">
                                        <span className="form-label">{cacheStore.GetString("SitesLabel")}</span>
                                    </div>
                                    <div className="grow">
                                        {this.props.Model.get("Sites") && (
                                            <select className="form-control myle-control minimal" onChange={e => this.onSiteSelectedChange(e)} value={privateBillModel.get("SelectedSiteId")}>
                                                {this.props.Model.get("Sites").toArray().map(site =>
                                                    <option key={site.Id} value={site.Id}>{site.Name}</option>
                                                )}
                                            </select>
                                        )}
                                    </div>
                                </div>
                            )}

                            {costCenterList != null && costCenterList.size > 0 && (
                                <div className="flex-container private-billing">
                                    <div className="title">
                                        <span className="form-label">{cacheStore.GetString("CostCentreLabel")}</span>
                                    </div>
                                    <div className="grow">
                                        <select className="form-control myle-control minimal" onChange={e => this.onCostCenterChange(e)} value={privateBillModel.get("SelectedCostCenterId")}>
                                            {costCenterList.map(costCenter =>
                                                <option key={costCenter.ID} value={costCenter.ID}>{PrivateBillCostCenter.GetDisplayName(costCenter)}</option>
                                            )}
                                        </select>
                                    </div>
                                </div>
                            )}

                            <div className="flex-container private-billing">
                                <div className="title">
                                    <span className="form-label">{cacheStore.GetString("ProviderLabel")}</span>
                                </div>
                                <div className="grow has-error">
                                    <PersonSearchAutocompleteCustom
                                        useBlurEvent={false}
                                        disabled={false}
                                        componentId="add_privatebill_clinicians"
                                        selectedItemList={privateBillModel.get("SelectedClinicians") || [].toImmutable()}
                                        valid={this.isProviderFieldValid}
                                        selectionOp={this.props.SelectClinician}
                                        clearOp={this.props.SelectClinician}
                                        searchStrategy={this.state.strategy}
                                        focusOnLoad={false}
                                        closeDropDownAfterSelection={true}
                                        dataCy="privateBillEdit-provider"
                                    />
                                </div>
                                <div>
                                    {this.props.Model.get("IsProviderLocked") === true &&
                                        <img style={{ height: "40px", width: "42px", paddingBottom: "5px" }} src={img_locked} alt={cacheStore.GetString("RememberLabel")} onClick={() => this.lockProvider()} />
                                    }
                                    {this.props.Model.get("IsProviderLocked") === false &&
                                        <img style={{ height: "40px", width: "42px", paddingBottom: "5px" }} src={img_unlocked} alt={cacheStore.GetString("RememberLabel")} onClick={() => this.lockProvider()} />
                                    }

                                </div>
                            </div>

                            <div className="flex-container private-billing">
                                <div className="title">
                                    <span className="form-label">{cacheStore.GetString("PaymentMethodLabel")}</span>
                                </div>
                                <div className="grow">
                                    <select className="form-control myle-control minimal" onChange={e => this.onPaymentMethodChange(e)} value={privateBillModel.get("SelectedPaymentMethod")} data-cy="privateBillEdit-paymentMethod">
                                        {Object.keys(paymentMethodDictionnary).map(x =>
                                            <option key={x} value={x}>{paymentMethodDictionnary[x]}</option>
                                        )}
                                    </select>
                                </div>
                            </div>
                            {cacheStore.ClinicSettings.EnablePrivateBillRecipient && (
                                <div className="flex-container private-billing">
                                    <div className="title">
                                        <span className="form-label" data-cy="PrivateBillingBilledToFieldTitle">{cacheStore.GetString("BilledToLabel")}</span>
                                    </div>
                                    <div className="grow">
                                        <select
                                            className="form-control myle-control minimal"
                                            onChange={e => this.onBillToChange(e)}
                                            defaultValue={privateBillModel.get("SelectedBillBilledTo")}
                                            data-cy="PrivateBillingBilledToField"
                                        >
                                            {Object.keys(billToDictionnary).map(idOfPaymentMethod =>
                                                <option key={billToDictionnary[idOfPaymentMethod]} value={idOfPaymentMethod}>{billToDictionnary[idOfPaymentMethod]}</option>
                                            )}
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>
                        {showRecipientDetails && (
                            <div className="col-md-6">
                                <RecipientDetails
                                    recipientDetails={privateBillModel.get("SelectedBillRecipientDetails")}
                                    onChange={recipientDetails => this.onRecipientDetailsChange(recipientDetails)}
                                    isEditMode={true}
                                />
                            </div>
                        )}
                    </div>
                    <hr />

                    <div style={{ paddingLeft: "49px" }} data-cy="privateBillEdit-billingService">
                        {serviceEditComponents.map((c: JSX.Element, idx: number) => (
                            <div key={idx + "__control"}>{c}</div>
                        ))}
                    </div>

                    <div style={{ textAlign: "right", padding: "0 0 10px" }} />

                    {(hasCostPermission || sessionStore.getCurrentEmployee().isActiveSiteMexico() === false) && (
                        <div className="flex-container totals rew">
                            <div className="totals-col" data-cy="privateBillEdit-amountSection">
                                {this.renderLowerRow(cacheStore.GetString("TotalAmountLabel"), "$ " + privateBillModel.get("NewBillTotalAmount").toFixed(2), "")}
                                <div className="flex-container discount">
                                    <div className="label">
                                        <span>
                                            {cacheStore.GetString("DiscountLabel")}
                                        </span>
                                    </div>
                                    <div className="flex-container" style={{ justifyContent: "flex-end" }}>
                                        {privateBillModel.get("IsCashSelected") === true && (
                                            <div className="flex-container curr" style={{ marginLeft: "5px", marginRight: "5px", alignItems: "center" }}>
                                                <strong className="pointer">$</strong>
                                                <span className="separator">|</span>
                                                <span className="pointer light-blue" onClick={() => this.onDiscountTypeChange()}>%</span>
                                            </div>
                                        )}
                                        {privateBillModel.get("IsCashSelected") === false && (
                                            <div className="flex-container curr" style={{ marginLeft: "5px", marginRight: "5px", alignItems: "center" }}>
                                                <span className="pointer light-blue" onClick={() => this.onDiscountTypeChange()}>$</span>
                                                <span className="separator">|</span>
                                                <strong className="pointer">%</strong>
                                            </div>
                                        )}
                                        <div className="input" style={{ width: "100px" }}>
                                            <input type="number" className="form-control" data-cy="privateBillEdit-discount" min="0" value={String(privateBillModel.get("NewBillDiscount"))} onChange={e => this.onDiscountChange(e)} />
                                        </div>
                                    </div>
                                </div>
                                {this.renderTaxes(privateBillModel, selectedSite)}
                                <hr />
                                {this.renderLowerRow(cacheStore.GetString("GrandTotalLabel").toUpperCase(), "$ " + privateBillModel.get("NewBillTotal").toFixed(2) + " " + cacheStore.defaultCurrencyUnit, "")}
                            </div>
                        </div>
                    )}

                    <div>
                        <label>{cacheStore.GetString("NotesLabel")}</label>
                        <textarea className="form-control vresize" onChange={e => this.onNotesChange(e)} id="history_create_invalidation" disabled={false} rows={4} style={{ maxHeight: "300px", marginTop: "5px" }} data-cy="privateBillEdit-notes">
                            {privateBillModel.get("Notes")}
                        </textarea>
                    </div>
                </div>
            </div>
        );
    }

    private renderTaxes(privateBillModel: IPatientPrivateBillModel, selectedSite: SitesModel): JSX.Element {
        return (
            <div>
                {(selectedSite.isHst() || !!privateBillModel.get("NewBillHSTTotal")) && this.renderLowerRow(cacheStore.GetString("HstMLabel"), "$ " + privateBillModel.get("NewBillHSTTotal").toFixed(2), "", "" + Number(privateBillModel.get("HSTPercent")).toFixed(2), false)}
                {(selectedSite.isGst() || !!privateBillModel.get("NewBillGSTTotal")) && this.renderLowerRow(cacheStore.GetString("GstMLabel"), "$ " + privateBillModel.get("NewBillGSTTotal").toFixed(2), "", "" + Number(privateBillModel.get("GSTPercent")).toFixed(2), false)}
                {(selectedSite.isPstOrQst() || !!privateBillModel.get("NewBillQSTTotal")) && this.renderLowerRow(selectedSite.getPstOrQstLabel(), "$ " + privateBillModel.get("NewBillQSTTotal").toFixed(2), "", "" + Number(privateBillModel.get("QSTPercent")).toFixed(2), true)}
                {(!selectedSite.isCanadaSite() || !!privateBillModel.get("NewBillGenericTaxTotal")) && this.renderLowerRow(cacheStore.GetString("TaxLabel"), "$ " + privateBillModel.get("NewBillGenericTaxTotal").toFixed(2), "", "" + Number(privateBillModel.get("GenericTaxPercent")).toFixed(2), true)}
            </div>
        );
    }

    private renderLowerRow(label: string, formattedPrice: string, currency: string, percnt?: string, isTps?: boolean): JSX.Element {
        const site = cacheStore.SiteCache.filter(n => n.Model.Id === this.props.Model.get("SelectedSiteId"));
        let prc = "";
        if (isTps === true) {
            prc = percnt == null ? null : ` ${percnt}%${site[0].Model.GSTCode ? ` (${site[0].Model.GSTCode})` : ""}`;
        }
        else if (isTps === false) {
            prc = percnt != null ? " " + percnt + "%" + (site[0].Model.GSTCode ? ` (${site[0].Model.QSTCode})` : "") : null;
        }

        return (
            <div className="fl-x">
                <span>{label} {prc}</span>
                <strong style={{ whiteSpace: "nowrap" }}>{formattedPrice} {currency}</strong>
            </div>
        );
    }

    private onNotesChange(e: React.ChangeEvent<HTMLTextAreaElement>): void {
        sessionStore.dispatch(this.props.NotesChangeOp, { value: e.target.value });
    }

    private onTodayClick(e: React.MouseEvent<HTMLAnchorElement>): void {
        e.preventDefault();
        sessionStore.dispatch(this.props.TodayClickedOp);
    }

    private onSiteSelectedChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        sessionStore.dispatch(this.props.SiteSelectedOp, { value: e.target.value });
    }

    private onCostCenterChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        sessionStore.dispatch(this.props.CostCenterChangedOp, { value: e.target.value });
    }

    private onPaymentMethodChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        sessionStore.dispatch(this.props.PaymentMethodChangedOp, { value: e.target.value });
    }

    private onBillToChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        sessionStore.dispatch(this.props.BilledToChangeOp, { value: e.target.value });
    }

    private onDiscountChange(e: React.ChangeEvent<HTMLInputElement>): void {
        sessionStore.dispatch(this.props.DiscountChangedOp, { value: this.getClampedValue(e.target.value, 0) });
    }

    private onDiscountTypeChange(): void {
        sessionStore.dispatch(this.props.DiscountTypeChangeOp);
    }

    private lockProvider(): void {
        sessionStore.dispatch(this.props.LockProviderOp);
    }

    private getClampedValue(value: string, min: number): number {
        const parsed = parseFloat(value) || 0;
        return Math.max(parsed, min);
    }

    private onRecipientDetailsChange(recipientDetails: RecipientDetailsModel): void {
        sessionStore.dispatch(
            this.props.ChangeRecipientDetailsOp,
            {
                recipientDetails
            }
        );
    }

    private get isProviderFieldValid(): boolean {
        return cacheStore.ClinicSettings.EnablePrivateBillRecipient
            || !!(this.props.Model?.get("SelectedClinicians")?.toArray().length > 0);
    }
}

export { IPatientHistoryPrivateBillEditProps, PrivateBillEditControl };
